import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Expense, ExpenseCategory, CATEGORY_LABELS, CATEGORY_DESCRIPTIONS } from '../types/expense';
import { calculateAuditRisk, getRiskLevel } from '../utils/auditRisk';
import { getPremiumStatus, canAddExpense, unlockWithCode } from '../utils/paywall';
import { redirectToCheckout, checkPaymentStatus, handlePaymentSuccess } from '../utils/stripePayment';
import { exportExpensesToPDF } from '../utils/pdfExport';
import { exportExpensesToCSV, exportScheduleCToCSV } from '../utils/csvExport';
import { getCurrentUser, logoutUser } from '../utils/auth';
import PlaidLinkButton from '../components/PlaidLinkButton';
import TransactionImport from '../components/TransactionImport';
import { isBankConnected, disconnectBank } from '../utils/plaidIntegration';
import { calculateQuarterlyEstimates, getNextQuarterDue, type QuarterlyTaxEstimate } from '../utils/quarterlyTaxes';
import { getCurrentMileageRate, storeMileageEntry, getMileageEntries, deleteMileageEntry, getTotalMileageDeduction, getTotalMiles, type MileageEntry, calculateMileageDeduction } from '../utils/mileageTracking';
import { uploadReceipt, getReceiptsByExpenseId, deleteReceipt, formatFileSize, type Receipt } from '../utils/receiptStorage';
import { compareYears, getAvailableYears } from '../utils/yearComparison';
import { checkSubscriptionStatus, openCustomerPortal, formatSubscriptionDate, formatAmount, getSubscriptionStatusText, updatePremiumStatusWithSubscription, type SubscriptionStatus } from '../utils/subscriptionManager';
import { Invoice, InvoiceStats, Client } from '../types/invoice';
import { checkOverdueInvoices, generateRecurringInvoices } from '../utils/invoiceUtils';
import CreateInvoiceModal from '../components/CreateInvoiceModal';
import AIAssistant from '../components/AIAssistant';

// Helper to get user-specific storage keys
const getUserStorageKey = (baseKey: string, userId?: string) => {
  return userId ? `${baseKey}_${userId}` : baseKey;
};

type Tab = 'dashboard' | 'expenses' | 'reports' | 'tax-estimates' | 'mileage' | 'invoices';
type DateFilter = '30days' | '3months' | '6months' | 'year' | 'all' | 'custom';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

export default function ExpenseTrackerNew() {
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [income, setIncome] = useState<number>(0);
  const [isPremium, setIsPremium] = useState(false);
  const [currentUser, setCurrentUser] = useState(getCurrentUser());
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [showTransactionImport, setShowTransactionImport] = useState(false);
  const [bankConnected, setBankConnected] = useState(isBankConnected());
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<ExpenseCategory | 'all'>('all');
  const [dateFilter, setDateFilter] = useState<DateFilter>('3months');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    category: 'office-expense' as ExpenseCategory,
    description: '',
    businessPurpose: '',
  });
  
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState<'monthly' | 'annual'>('monthly');
  const [recurringMonths, setRecurringMonths] = useState(12);

  // Modal states
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showUnlockCodeModal, setShowUnlockCodeModal] = useState(false);
  const [unlockCode, setUnlockCode] = useState('');
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Premium feature states
  const [mileageEntries, setMileageEntries] = useState<MileageEntry[]>([]);
  const [quarterlyEstimates, setQuarterlyEstimates] = useState<QuarterlyTaxEstimate[]>([]);
  const [mileageForm, setMileageForm] = useState({
    date: new Date().toISOString().split('T')[0],
    startLocation: '',
    endLocation: '',
    miles: '',
    purpose: ''
  });
  const [comparisonYear1, setComparisonYear1] = useState(new Date().getFullYear());
  const [comparisonYear2, setComparisonYear2] = useState(new Date().getFullYear() - 1);
  const [uploadingReceipt, setUploadingReceipt] = useState<string | null>(null);
  const [receiptsByExpense, setReceiptsByExpense] = useState<Record<string, Receipt[]>>({});
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  
  // Invoice states
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);
  const [invoiceFilterStatus, setInvoiceFilterStatus] = useState<'all' | Invoice['status']>('all');
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState('');
  const [invoiceDateFilter, setInvoiceDateFilter] = useState<'all' | '30days' | '3months' | '6months' | 'year'>('all');
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [invoiceStats, setInvoiceStats] = useState<InvoiceStats>({
    totalInvoices: 0,
    totalRevenue: 0,
    paidInvoices: 0,
    pendingInvoices: 0,
    overdueInvoices: 0,
    averageInvoiceValue: 0,
  });
  const [stripeAccountId, setStripeAccountId] = useState<string | null>(null);
  const [stripeAccountStatus, setStripeAccountStatus] = useState<{
    connected: boolean;
    chargesEnabled: boolean;
    detailsSubmitted: boolean;
  }>({ connected: false, chargesEnabled: false, detailsSubmitted: false });
  const [loadingStripeConnect, setLoadingStripeConnect] = useState(false);
  
  // Current tax year
  const currentTaxYear = new Date().getFullYear();

  // Load data on mount and when user changes
  useEffect(() => {
    const user = getCurrentUser();
    setCurrentUser(user);
    setIsPremium(getPremiumStatus().isPremium);
    setBankConnected(isBankConnected(user?.email));

    // Load user-specific data
    const userId = user?.email;
    const expensesKey = getUserStorageKey('expense_tracker_expenses', userId);
    const incomeKey = getUserStorageKey('expense_tracker_income', userId);
    
    const stored = localStorage.getItem(expensesKey);
    if (stored) setExpenses(JSON.parse(stored));
    
    const storedIncome = localStorage.getItem(incomeKey);
    if (storedIncome) setIncome(parseFloat(storedIncome));

    // Load mileage entries for premium users
    if (userId) {
      setMileageEntries(getMileageEntries(userId));
      
      // Load receipts for all expenses
      const receipts: Record<string, Receipt[]> = {};
      const storedExpenses = stored ? JSON.parse(stored) : [];
      storedExpenses.forEach((expense: Expense) => {
        receipts[expense.id] = getReceiptsByExpenseId(expense.id, userId);
      });
      setReceiptsByExpense(receipts);
      
      // Load invoices and clients
      const storedInvoices = localStorage.getItem(getUserStorageKey('invoices', userId));
      if (storedInvoices) {
        let parsedInvoices = JSON.parse(storedInvoices);
        
        // Check for overdue invoices
        parsedInvoices = checkOverdueInvoices(parsedInvoices);
        
        // Generate recurring invoices if needed
        const newRecurringInvoices = generateRecurringInvoices(parsedInvoices);
        if (newRecurringInvoices.length > 0) {
          parsedInvoices = [...parsedInvoices, ...newRecurringInvoices];
          // Save updated invoices
          localStorage.setItem(getUserStorageKey('invoices', userId), JSON.stringify(parsedInvoices));
        }
        
        setInvoices(parsedInvoices);
        
        // Calculate invoice stats
        const totalInvoices = parsedInvoices.length;
        const paidInvoices = parsedInvoices.filter((inv: Invoice) => inv.status === 'paid').length;
        const pendingInvoices = parsedInvoices.filter((inv: Invoice) => inv.status === 'sent' || inv.status === 'viewed').length;
        const overdueInvoices = parsedInvoices.filter((inv: Invoice) => inv.status === 'overdue').length;
        const totalRevenue = parsedInvoices.filter((inv: Invoice) => inv.status === 'paid').reduce((sum: number, inv: Invoice) => sum + inv.total, 0);
        const averageInvoiceValue = totalInvoices > 0 ? totalRevenue / paidInvoices || 0 : 0;
        
        setInvoiceStats({
          totalInvoices,
          totalRevenue,
          paidInvoices,
          pendingInvoices,
          overdueInvoices,
          averageInvoiceValue,
        });
      }
      
      const storedClients = localStorage.getItem(getUserStorageKey('clients', userId));
      if (storedClients) {
        setClients(JSON.parse(storedClients));
      }
      
      // Load Stripe account ID
      const savedStripeAccountId = localStorage.getItem(getUserStorageKey('stripeAccountId', userId));
      if (savedStripeAccountId) {
        setStripeAccountId(savedStripeAccountId);
        checkStripeAccountStatus(savedStripeAccountId);
      }
    }

    // Check payment status
    const paymentResult = checkPaymentStatus();
    if (paymentResult.status === 'success') {
      handlePaymentSuccess(paymentResult.sessionId).then(() => {
        setIsPremium(true);
      }).catch(error => {
        alert('Error processing payment. Please contact support if the issue persists.');
      });
    }

    // Check active subscription status if user has Stripe customer ID
    const premiumStatus = getPremiumStatus();
    if (premiumStatus.stripeCustomerId) {
      setLoadingSubscription(true);
      checkSubscriptionStatus(premiumStatus.stripeCustomerId)
        .then((status) => {
          if (status) {
            setSubscriptionStatus(status);
            // Update premium status based on active subscription
            if (status.hasActiveSubscription && status.subscription) {
              updatePremiumStatusWithSubscription(
                premiumStatus.stripeCustomerId!,
                true,
                status.subscription
              );
              setIsPremium(true);
            } else if (!status.hasActiveSubscription) {
              // Subscription expired or payment failed - revoke premium
              // BUT keep customer ID so they can manage billing and restart
              updatePremiumStatusWithSubscription(
                premiumStatus.stripeCustomerId!,
                false
              );
              setIsPremium(false);
            }
          }
        })
        .catch(() => {
          // Silently fail - user can still use app
        })
        .finally(() => setLoadingSubscription(false));
    } else if (premiumStatus.isPremium && currentUser?.email) {
      // Premium user but missing customer ID - try to recover it
      import('../utils/subscriptionManager').then(({ findCustomerByEmail }) => {
        findCustomerByEmail(currentUser.email).then((result) => {
          if (result?.found && result.customerId) {
            // Update premium status with recovered customer ID
            const updatedStatus = {
              ...premiumStatus,
              stripeCustomerId: result.customerId,
              subscriptionId: result.subscriptionId,
              subscriptionStatus: result.subscriptionStatus,
              currentPeriodEnd: result.currentPeriodEnd,
              cancelAtPeriodEnd: result.cancelAtPeriodEnd,
              isPremium: result.hasActiveSubscription,
            };
            localStorage.setItem('expense_tracker_premium', JSON.stringify(updatedStatus));
            setIsPremium(result.hasActiveSubscription);
          }
        }).catch(() => {
          // Silently fail - user retains premium status
        });
      });
    }
  }, []);

  // Update bank connection status when user changes
  useEffect(() => {
    if (currentUser?.email) {
      // Small delay to ensure localStorage is ready
      const checkConnection = () => {
        const connected = isBankConnected(currentUser.email);
        setBankConnected(connected);
      };
      
      checkConnection();
      // Double-check after a brief moment in case of timing issues
      const timer = setTimeout(checkConnection, 100);
      return () => clearTimeout(timer);
    } else {
      setBankConnected(false);
    }
  }, [currentUser]);

  // Save expenses automatically (user-specific)
  useEffect(() => {
    const userId = currentUser?.email;
    const expensesKey = getUserStorageKey('expense_tracker_expenses', userId);
    if (expenses.length > 0) {
      localStorage.setItem(expensesKey, JSON.stringify(expenses));
    }
  }, [expenses, currentUser]);

  // Save income automatically (user-specific)
  useEffect(() => {
    const userId = currentUser?.email;
    const incomeKey = getUserStorageKey('expense_tracker_income', userId);
    if (income > 0) {
      localStorage.setItem(incomeKey, income.toString());
    }
  }, [income, currentUser]);

  // Close settings dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showSettingsDropdown && !target.closest('.settings-dropdown-container')) {
        setShowSettingsDropdown(false);
      }
    };

    if (showSettingsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSettingsDropdown]);

  const stats = useMemo(() => calculateAuditRisk(expenses, income), [expenses, income]);
  const riskLevel = getRiskLevel(stats.auditRiskScore);

  // Date filtering
  const getFilteredExpensesByDate = useMemo(() => {
    const now = new Date();
    let startDate: Date;

    switch (dateFilter) {
      case '30days':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '3months':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case '6months':
        startDate = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      case 'custom':
        if (!customStartDate) return expenses;
        startDate = new Date(customStartDate);
        const endDate = customEndDate ? new Date(customEndDate) : now;
        return expenses.filter(exp => {
          const expDate = new Date(exp.date);
          return expDate >= startDate && expDate <= endDate;
        });
      case 'all':
      default:
        return expenses;
    }

    return expenses.filter(exp => new Date(exp.date) >= startDate);
  }, [expenses, dateFilter, customStartDate, customEndDate]);

  // Calculate stats for filtered data
  const filteredStats = useMemo(() => ({
    totalExpenses: getFilteredExpensesByDate.reduce((sum, exp) => sum + exp.amount, 0),
    count: getFilteredExpensesByDate.length,
    avgPerDay: getFilteredExpensesByDate.length > 0 
      ? getFilteredExpensesByDate.reduce((sum, exp) => sum + exp.amount, 0) / 
        Math.max(1, Math.ceil((Date.now() - new Date(getFilteredExpensesByDate[getFilteredExpensesByDate.length - 1]?.date || Date.now()).getTime()) / (24 * 60 * 60 * 1000)))
      : 0,
    largestExpense: getFilteredExpensesByDate.length > 0 
      ? Math.max(...getFilteredExpensesByDate.map(e => e.amount))
      : 0,
  }), [getFilteredExpensesByDate]);

  // Calculate daily spending for filtered period
  const dailySpending = useMemo(() => {
    const dailyData: { [key: string]: number } = {};
    getFilteredExpensesByDate.forEach(exp => {
      const day = exp.date; // YYYY-MM-DD
      dailyData[day] = (dailyData[day] || 0) + exp.amount;
    });
    return Object.entries(dailyData)
      .map(([date, amount]) => ({ date, amount }))
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-30); // Last 30 days of data
  }, [getFilteredExpensesByDate]);

  // Top merchants
  const topMerchants = useMemo(() => {
    const merchantData: { [key: string]: number } = {};
    getFilteredExpensesByDate.forEach(exp => {
      const merchant = exp.description || 'Unknown';
      merchantData[merchant] = (merchantData[merchant] || 0) + exp.amount;
    });
    return Object.entries(merchantData)
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10);
  }, [getFilteredExpensesByDate]);

  // Category breakdown for filtered data
  const filteredCategoryData = useMemo(() => {
    const categoryTotals: { [key: string]: number } = {};
    getFilteredExpensesByDate.forEach(exp => {
      categoryTotals[exp.category] = (categoryTotals[exp.category] || 0) + exp.amount;
    });
    return Object.entries(categoryTotals)
      .map(([category, amount]) => ({
        name: CATEGORY_LABELS[category as ExpenseCategory],
        value: amount,
        category
      }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [getFilteredExpensesByDate]);

  // Calculate monthly spending
  const monthlySpending = useMemo(() => {
    const monthlyData: { [key: string]: number } = {};
    expenses.forEach(exp => {
      const month = exp.date.substring(0, 7); // YYYY-MM
      monthlyData[month] = (monthlyData[month] || 0) + exp.amount;
    });
    return Object.entries(monthlyData).map(([month, amount]) => ({ month, amount })).sort((a, b) => a.month.localeCompare(b.month));
  }, [expenses]);

  // Calculate category breakdown
  const categoryData = useMemo(() => {
    return Object.entries(stats.expensesByCategory).map(([category, amount]) => ({
      name: CATEGORY_LABELS[category as ExpenseCategory],
      value: amount,
      category
    })).filter(d => d.value > 0).sort((a, b) => b.value - a.value);
  }, [stats.expensesByCategory]);

  // Stripe Connect functions
  const connectStripeAccount = async () => {
    if (!currentUser) return;
    
    setLoadingStripeConnect(true);
    try {
      const response = await fetch('/.netlify/functions/create-stripe-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: currentUser.email,
          email: currentUser.email,
          businessName: currentUser.email, // Could be enhanced with actual business name
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        localStorage.setItem(getUserStorageKey('stripeAccountId', currentUser.email), data.accountId);
        setStripeAccountId(data.accountId);
        // Redirect to Stripe onboarding
        window.location.href = data.onboardingUrl;
      } else {
        alert('Failed to create Stripe account: ' + data.error);
      }
    } catch (error) {
      console.error('Error connecting Stripe:', error);
      alert('Failed to connect Stripe account');
    } finally {
      setLoadingStripeConnect(false);
    }
  };
  
  const checkStripeAccountStatus = async (accountId: string) => {
    try {
      const response = await fetch('/.netlify/functions/get-stripe-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setStripeAccountStatus({
          connected: true,
          chargesEnabled: data.chargesEnabled || false,
          detailsSubmitted: data.detailsSubmitted || false,
        });
      }
    } catch (error) {
      console.error('Error checking Stripe account:', error);
    }
  };
  
  const openStripeDashboard = async () => {
    if (!stripeAccountId) return;
    
    try {
      const response = await fetch('/.netlify/functions/stripe-dashboard-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId: stripeAccountId }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error opening Stripe dashboard:', error);
    }
  };

  const handleDeleteInvoice = (invoiceId: string) => {
    if (!confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) {
      return;
    }
    
    const updatedInvoices = invoices.filter(inv => inv.id !== invoiceId);
    setInvoices(updatedInvoices);
    localStorage.setItem(getUserStorageKey('invoices', currentUser?.email), JSON.stringify(updatedInvoices));
    
    // Recalculate stats
    const totalInvoices = updatedInvoices.length;
    const paidInvoices = updatedInvoices.filter(inv => inv.status === 'paid').length;
    const pendingInvoices = updatedInvoices.filter(inv => inv.status === 'sent' || inv.status === 'viewed').length;
    const overdueInvoices = updatedInvoices.filter(inv => inv.status === 'overdue').length;
    const totalRevenue = updatedInvoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.total, 0);
    const averageInvoiceValue = totalInvoices > 0 ? totalRevenue / paidInvoices || 0 : 0;
    
    setInvoiceStats({
      totalInvoices,
      totalRevenue,
      paidInvoices,
      pendingInvoices,
      overdueInvoices,
      averageInvoiceValue,
    });
  };

  const handleEditInvoice = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    setShowCreateInvoiceModal(true);
  };

  const handleUpdateInvoice = (updatedInvoice: Invoice) => {
    const updatedInvoices = invoices.map(inv => 
      inv.id === updatedInvoice.id ? updatedInvoice : inv
    );
    setInvoices(updatedInvoices);
    localStorage.setItem(getUserStorageKey('invoices', currentUser?.email), JSON.stringify(updatedInvoices));
    setEditingInvoice(null);
  };

  const filterInvoicesByDate = (invoices: Invoice[]) => {
    if (invoiceDateFilter === 'all') return invoices;
    
    const now = new Date();
    const daysAgo = invoiceDateFilter === '30days' ? 30 :
                    invoiceDateFilter === '3months' ? 90 :
                    invoiceDateFilter === '6months' ? 180 : 365;
    const cutoffDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);
    
    return invoices.filter(invoice => new Date(invoice.createdAt) >= cutoffDate);
  };

  // Filtered expenses
  const filteredExpenses = useMemo(() => {
    return expenses.filter(exp => {
      const matchesSearch = exp.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           exp.businessPurpose?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = filterCategory === 'all' || exp.category === filterCategory;
      return matchesSearch && matchesCategory;
    }).sort((a, b) => b.date.localeCompare(a.date));
  }, [expenses, searchQuery, filterCategory]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!canAddExpense(expenses.length) && !isRecurring) {
      setShowUpgradeModal(true);
      return;
    }

    if (editingExpense) {
      setExpenses(expenses.map(exp => 
        exp.id === editingExpense.id 
          ? { ...exp, ...formData, amount: parseFloat(formData.amount) }
          : exp
      ));
      setEditingExpense(null);
    } else {
      // Handle recurring expenses
      if (isRecurring) {
        const amount = parseFloat(formData.amount);
        const startDate = new Date(formData.date);
        const newExpenses: Expense[] = [];
        
        const occurrences = recurringFrequency === 'monthly' ? recurringMonths : 1;
        const monthIncrement = recurringFrequency === 'monthly' ? 1 : 12;
        
        for (let i = 0; i < occurrences; i++) {
          const expenseDate = new Date(startDate);
          expenseDate.setMonth(startDate.getMonth() + (i * monthIncrement));
          
          newExpenses.push({
            id: `${Date.now()}_${i}`,
            date: expenseDate.toISOString().split('T')[0],
            amount: amount,
            category: formData.category,
            description: `${formData.description} ${recurringFrequency === 'monthly' ? `(Month ${i + 1})` : `(Year ${i + 1})`}`,
            businessPurpose: formData.businessPurpose,
            receiptUrl: '',
            createdAt: new Date().toISOString(),
            userId: currentUser?.email
          });
        }
        
        setExpenses([...expenses, ...newExpenses]);
        alert(`Added ${occurrences} recurring ${recurringFrequency} expenses!`);
      } else {
        // Single expense
        const newExpense: Expense = {
          id: Date.now().toString(),
          date: formData.date,
          amount: parseFloat(formData.amount),
          category: formData.category,
          description: formData.description,
          businessPurpose: formData.businessPurpose,
          receiptUrl: '',
          createdAt: new Date().toISOString(),
          userId: currentUser?.email
        };
        setExpenses([...expenses, newExpense]);
      }
    }
    
    setFormData({
      date: new Date().toISOString().split('T')[0],
      amount: '',
      category: 'office-expense',
      description: '',
      businessPurpose: '',
    });
    setIsRecurring(false);
    setRecurringMonths(12);
  };

  const handleDelete = (id: string) => {
    if (confirm('Delete this expense?')) {
      setExpenses(expenses.filter(e => e.id !== id));
    }
  };

  const handleEdit = (expense: Expense) => {
    setEditingExpense(expense);
    setFormData({
      date: expense.date,
      amount: expense.amount.toString(),
      category: expense.category,
      description: expense.description,
      businessPurpose: expense.businessPurpose || '',
    });
    setActiveTab('expenses');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  const exportToPDF = () => {
    if (!isPremium) {
      setShowUpgradeModal(true);
      return;
    }
    exportExpensesToPDF(expenses, income, stats, new Date().getFullYear());
  };

  const exportToCSV = () => {
    exportExpensesToCSV(expenses, income, new Date().getFullYear());
  };

  const exportScheduleC = () => {
    exportScheduleCToCSV(stats.expensesByCategory, new Date().getFullYear());
  };

  const handleLogout = () => {
    // Disconnect bank for this user
    disconnectBank(currentUser?.email);
    // Clear auth using logoutUser (triggers auth-change event for header)
    logoutUser();
    // Reset state
    setCurrentUser(null);
    setBankConnected(false);
    // Redirect to home
    window.location.href = '/';
  };

  const handleDisconnectBank = () => {
    if (confirm('Are you sure you want to disconnect your bank account?')) {
      disconnectBank(currentUser?.email);
      setBankConnected(false);
      setShowSettingsDropdown(false);
    }
  };

  return (
    <>
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold mb-1">Expense Tracker</h1>
              <p className="text-sm md:text-base text-primary-100">Professional expense management for 1099 contractors</p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 md:gap-3 w-full sm:w-auto">
              {isPremium && !bankConnected && (
                <PlaidLinkButton 
                  userId={currentUser?.email}
                  onSuccess={() => {
                    setBankConnected(true);
                    setShowTransactionImport(true);
                  }}
                  buttonText="Connect Bank"
                  className="px-4 py-2 bg-white text-primary-600 rounded-lg font-semibold hover:bg-primary-50 transition-colors w-full sm:w-auto text-center"
                />
              )}
              {isPremium && bankConnected && (
                <button
                  onClick={() => setShowTransactionImport(true)}
                  className="px-4 py-2 bg-white text-primary-600 rounded-lg font-semibold hover:bg-primary-50 transition-colors w-full sm:w-auto"
                >
                  Import Transactions
                </button>
              )}
              {!isPremium && (
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="px-6 py-2 bg-yellow-400 text-gray-900 rounded-lg font-bold hover:bg-yellow-300 transition-colors shadow-lg"
                >
                  Start FREE Trial
                </button>
              )}
              {!currentUser && (
                <a
                  href="/tools/expense-tracker/login"
                  className="px-6 py-2 bg-white text-primary-600 rounded-lg font-bold hover:bg-primary-50 transition-colors"
                >
                  Login
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Mobile scroll indicator */}
          <div className="md:hidden absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none flex items-center justify-end pr-2">
            <svg className="w-4 h-4 text-gray-400 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
          <div className="flex gap-3 md:gap-6 overflow-x-auto mobile-tab-scroll md:scrollbar-hide scroll-smooth">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`py-3 md:py-4 px-2 md:px-3 border-b-2 font-semibold transition-colors whitespace-nowrap text-sm md:text-base ${
                activeTab === 'dashboard'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('expenses')}
              className={`py-3 md:py-4 px-2 md:px-3 border-b-2 font-semibold transition-colors whitespace-nowrap text-sm md:text-base ${
                activeTab === 'expenses'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Expenses ({expenses.length})
            </button>
            <button
              onClick={() => setActiveTab('reports')}
              className={`py-3 md:py-4 px-2 md:px-3 border-b-2 font-semibold transition-colors whitespace-nowrap text-sm md:text-base ${
                activeTab === 'reports'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Reports
            </button>
            <button
              onClick={() => setActiveTab('invoices')}
              className={`py-3 md:py-4 px-2 md:px-3 border-b-2 font-semibold transition-colors whitespace-nowrap text-sm md:text-base ${
                activeTab === 'invoices'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Invoices ({invoices.length})
            </button>
            <button
              onClick={() => setActiveTab('mileage')}
              className={`py-3 md:py-4 px-2 md:px-3 border-b-2 font-semibold transition-colors whitespace-nowrap text-sm md:text-base ${
                activeTab === 'mileage'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Mileage
            </button>
            {isPremium && (
              <button
                onClick={() => setActiveTab('tax-estimates')}
                className={`py-3 md:py-4 px-2 md:px-3 border-b-2 font-semibold transition-colors whitespace-nowrap text-sm md:text-base ${
                  activeTab === 'tax-estimates'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                💰 Tax Estimates
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">

          {/* DASHBOARD TAB */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              
              {/* Income Input - Always Visible */}
              <div className="bg-white rounded-xl p-4 md:p-6 shadow-lg border-2 border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                  <h2 className="text-lg md:text-xl font-bold text-gray-900">Your {currentTaxYear} Income</h2>
                  {income > 0 && (
                    <span className="text-xs md:text-sm text-green-600 font-semibold">✓ Income Set</span>
                  )}
                </div>
                <div className="max-w-md">
                  <div className="relative">
                    <span className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-lg md:text-xl">$</span>
                    <input
                      type="number"
                      value={income || ''}
                      onChange={(e) => setIncome(parseFloat(e.target.value) || 0)}
                      className="w-full pl-9 md:pl-10 pr-4 py-3 md:py-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-lg md:text-xl font-bold"
                      placeholder="100000"
                    />
                  </div>
                  <p className="text-xs md:text-sm text-gray-600 mt-2">Enter your {currentTaxYear} 1099 income to calculate expense ratios and audit risk</p>
                </div>
              </div>

              {/* Premium Upgrade CTA for Free Users */}
              {currentUser && !isPremium && (
                <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-pink-500 rounded-xl p-6 md:p-8 text-white shadow-2xl border-4 border-yellow-300 relative overflow-hidden">
                  <div className="absolute top-0 right-0 text-9xl opacity-10">🎁</div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-4xl">✨</span>
                      <div>
                        <h3 className="text-2xl md:text-3xl font-black mb-1">Start Your 14-Day FREE Trial!</h3>
                        <p className="text-yellow-50 font-semibold">No charge for 14 days • Cancel anytime</p>
                      </div>
                    </div>
                    
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 mb-5">
                      <p className="text-lg md:text-xl font-bold mb-4">🚀 Unlock Premium Features Today:</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                          <div>
                            <div className="font-bold">Unlimited Expenses</div>
                            <div className="text-sm text-yellow-50">Track everything, no limits</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                          <div>
                            <div className="font-bold">Bank Integration</div>
                            <div className="text-sm text-yellow-50">Auto-import transactions</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                          <div>
                            <div className="font-bold">AI Assistant</div>
                            <div className="text-sm text-yellow-50">Smart categorization & advice</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                          <div>
                            <div className="font-bold">Quarterly Tax Estimates</div>
                            <div className="text-sm text-yellow-50">Never miss a payment</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                          <div>
                            <div className="font-bold">Full Audit Analysis</div>
                            <div className="text-sm text-yellow-50">Detailed risk breakdown</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                          <div>
                            <div className="font-bold">PDF/CSV Exports</div>
                            <div className="text-sm text-yellow-50">Professional tax reports</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      <button
                        onClick={() => setShowUpgradeModal(true)}
                        className="w-full sm:flex-1 px-8 py-4 bg-white text-orange-600 rounded-xl font-black text-lg hover:bg-yellow-50 transition-all shadow-2xl transform hover:scale-105 border-4 border-white"
                      >
                        Start FREE Trial • $0 Today
                      </button>
                      <div className="text-center sm:text-left">
                        <div className="text-2xl font-black">$5.99/mo</div>
                        <div className="text-sm text-yellow-50">after 14 days</div>
                      </div>
                    </div>

                    <p className="text-xs text-yellow-50 mt-4 text-center">
                      Card required but not charged for 14 days • Save hundreds on tax prep
                    </p>
                  </div>
                </div>
              )}

              {/* Bank Connection CTA - Show for all logged-in users */}
              {currentUser && !bankConnected && (
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 md:p-8 text-white shadow-xl">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                    <div className="flex-1">
                      <h3 className="text-xl md:text-2xl font-bold mb-2">Connect Your Bank Account</h3>
                      <p className="text-sm md:text-base text-blue-100 mb-4">Automatically import transactions and categorize expenses with AI</p>
                      <ul className="space-y-2 text-sm md:text-base text-blue-50">
                        <li>✓ Secure OAuth connection via Plaid</li>
                        <li>✓ AI-powered categorization (85-95% accuracy)</li>
                        <li>✓ One-click import of transactions</li>
                        {!isPremium && <li>✓ Upgrade for unlimited expenses</li>}
                      </ul>
                    </div>
                    <PlaidLinkButton 
                      userId={currentUser?.email}
                      onSuccess={() => {
                        setBankConnected(true);
                        setShowTransactionImport(true);
                      }}
                      buttonText="Connect Now"
                      className="px-6 md:px-8 py-3 md:py-4 bg-white text-blue-600 rounded-lg font-bold hover:bg-blue-50 transition-colors shadow-lg text-base md:text-lg w-full lg:w-auto text-center whitespace-nowrap"
                    />
                  </div>
                </div>
              )}

              {/* Bank Connected Status - Show for all logged-in users */}
              {currentUser && bankConnected && (
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-6 text-white shadow-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="text-3xl">✓</div>
                      <div>
                        <h3 className="text-xl font-bold">Bank Connected</h3>
                        <p className="text-green-100">Ready to import transactions</p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
                      <button
                        onClick={() => setShowTransactionImport(true)}
                        className="px-4 md:px-6 py-2 md:py-3 bg-white text-green-600 rounded-lg font-bold hover:bg-green-50 transition-colors shadow-lg w-full sm:w-auto text-center"
                      >
                        📥 Import Transactions
                      </button>
                      <button
                        onClick={() => {
                          if (confirm('Disconnect your bank account?')) {
                            disconnectBank();
                            setBankConnected(false);
                          }
                        }}
                        className="px-4 py-2 md:py-3 bg-white/20 text-white rounded-lg font-semibold hover:bg-white/30 transition-colors w-full sm:w-auto"
                      >
                        Disconnect
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Login prompt for non-logged-in users */}
              {!currentUser && (
                <div className="bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl p-6 md:p-8 text-white shadow-xl">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                    <div className="flex-1">
                      <h3 className="text-xl md:text-2xl font-bold mb-2">🔐 Create Account to Connect Your Bank</h3>
                      <p className="text-sm md:text-base text-purple-100 mb-4">Sign up to unlock bank integration and advanced features</p>
                      <ul className="space-y-2 text-sm md:text-base text-purple-50">
                        <li>✓ Connect your bank account securely</li>
                        <li>✓ Auto-import and categorize transactions</li>
                        <li>✓ Track expenses and audit risk</li>
                        <li>✓ 14-day free trial included</li>
                      </ul>
                    </div>
                    <div className="flex flex-col gap-3 w-full lg:w-auto">
                      <a
                        href="/tools/expense-tracker/signup"
                        className="px-6 md:px-8 py-3 md:py-4 bg-white text-purple-600 rounded-lg font-bold hover:bg-purple-50 transition-colors shadow-lg text-base md:text-lg text-center whitespace-nowrap"
                      >
                        Sign Up Free
                      </a>
                      <a
                        href="/tools/expense-tracker/login"
                        className="px-6 md:px-8 py-2 md:py-3 bg-white/20 text-white rounded-lg font-semibold hover:bg-white/30 transition-colors text-center"
                      >
                        Log In
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* High-Level Category Summary - Premium Feature */}
              {income > 0 && expenses.length > 0 && (
                <>
                  {/* Audit Risk Score Card */}
                  <div className={`rounded-xl p-6 shadow-lg ${
                    riskLevel.level === 'High' ? 'bg-gradient-to-r from-red-500 to-orange-500' :
                    riskLevel.level === 'Moderate' ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                    'bg-gradient-to-r from-green-500 to-emerald-600'
                  } text-white`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-bold mb-1">🎯 Overall Risk Level</h3>
                        <div className="text-4xl font-bold mb-2">{riskLevel.level}</div>
                        <div className="text-white/90">{riskLevel.description}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-6xl font-bold">{stats.auditRiskScore}</div>
                        <div className="text-sm opacity-90">Risk Score</div>
                        <button
                          onClick={() => setActiveTab('reports')}
                          className="mt-3 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition-colors"
                        >
                          Full Analysis
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-4 md:p-8 shadow-lg">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">Deduction Summary by Category</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mb-4 md:mb-6">
                    {categoryData
                      .sort((a, b) => b.value - a.value)
                      .map((cat) => (
                      <div key={cat.category} className="border-2 border-gray-200 rounded-lg p-3 md:p-5 hover:border-blue-400 transition-all hover:shadow-md">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs md:text-sm font-bold text-gray-600 uppercase truncate pr-2">{cat.name}</span>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-semibold whitespace-nowrap">
                            {((cat.value / filteredStats.totalExpenses) * 100).toFixed(0)}%
                          </span>
                        </div>
                        <div className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">${cat.value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all" 
                            style={{width: `${Math.min((cat.value / filteredStats.totalExpenses) * 100, 100)}%`}}
                          ></div>
                        </div>
                        <div className="text-xs text-gray-500 mt-2">
                          {expenses.filter(e => e.category === cat.category).length} expense{expenses.filter(e => e.category === cat.category).length !== 1 ? 's' : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Total Summary */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-4 md:p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
                      <div>
                        <div className="text-xs md:text-sm text-gray-600 mb-1">Total Deductions</div>
                        <div className="text-2xl md:text-3xl font-bold text-gray-900">
                          ${filteredStats.totalExpenses.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs md:text-sm text-gray-600 mb-1">Tax Savings (30% bracket)</div>
                        <div className="text-2xl md:text-3xl font-bold text-green-600">
                          ${(filteredStats.totalExpenses * 0.30).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs md:text-sm text-gray-600 mb-1">Net Income</div>
                        <div className="text-2xl md:text-3xl font-bold text-blue-600">
                          ${(income - filteredStats.totalExpenses).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Top 3 Categories Callout */}
                  <div className="mt-6 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                    <div className="font-bold text-blue-900 mb-2">Top 3 Expense Categories:</div>
                    <div className="text-sm text-blue-900">
                      {categoryData.sort((a, b) => b.value - a.value).slice(0, 3).map((cat, idx) => (
                        <div key={cat.category} className="flex justify-between py-1">
                          <span>{idx + 1}. {cat.name}</span>
                          <span className="font-bold">${cat.value.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                </>
              )}

              {/* Next Tax Deadline Widget - Premium Feature */}
              {isPremium && income > 0 && (() => {
                const nextDue = getNextQuarterDue();
                return nextDue && (
                  <div className={`rounded-xl p-6 shadow-lg ${
                    nextDue.daysUntil <= 30 ? 'bg-gradient-to-r from-red-500 to-orange-500' :
                    nextDue.daysUntil <= 60 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                    'bg-gradient-to-r from-green-500 to-emerald-600'
                  } text-white`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-bold mb-1">⏰ Next Quarterly Tax Payment</h3>
                        <div className="text-3xl font-bold">{nextDue.quarter} Due: {nextDue.dueDate}</div>
                        <div className="text-white/90 mt-2">
                          {nextDue.daysUntil <= 7 ? '🚨 Due this week!' :
                           nextDue.daysUntil <= 30 ? '⚠️ Due this month' :
                           '✓ On track'}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-5xl font-bold">{nextDue.daysUntil}</div>
                        <div className="text-sm opacity-90">days left</div>
                        <button
                          onClick={() => setActiveTab('tax-estimates')}
                          className="mt-3 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition-colors"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Date Filter Controls - Rocket Money Style */}
              {income > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-lg">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-semibold text-gray-700">📅 Time Period:</span>
                      <button
                        onClick={() => setDateFilter('30days')}
                        className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                          dateFilter === '30days' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Last 30 Days
                      </button>
                      <button
                        onClick={() => setDateFilter('3months')}
                        className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                          dateFilter === '3months' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        3 Months
                      </button>
                      <button
                        onClick={() => setDateFilter('6months')}
                        className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                          dateFilter === '6months' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        6 Months
                      </button>
                      <button
                        onClick={() => setDateFilter('year')}
                        className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                          dateFilter === 'year' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        This Year
                      </button>
                      <button
                        onClick={() => setDateFilter('all')}
                        className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                          dateFilter === 'all' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        All Time
                      </button>
                      <button
                        onClick={() => setDateFilter('custom')}
                        className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                          dateFilter === 'custom' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Custom Range
                      </button>
                    </div>

                    {dateFilter === 'custom' && (
                      <div className="mt-4 flex gap-3 items-center flex-wrap">
                        <label className="text-sm font-semibold text-gray-700">From:</label>
                        <input
                          type="date"
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                          className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <label className="text-sm font-semibold text-gray-700">To:</label>
                        <input
                          type="date"
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e.target.value)}
                          className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    )}

                    {/* Spending Insights for Filtered Period */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6 mb-8">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                        <div className="text-sm text-blue-700 font-semibold mb-1">Total Spent</div>
                        <div className="text-2xl font-bold text-blue-900">
                          ${filteredStats.totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div className="text-xs text-blue-600 mt-1">{filteredStats.count} transactions</div>
                      </div>
                      <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                        <div className="text-sm text-green-700 font-semibold mb-1">Avg per Day</div>
                        <div className="text-2xl font-bold text-green-900">
                          ${filteredStats.avgPerDay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div className="text-xs text-green-600 mt-1">Daily average</div>
                      </div>
                      <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                        <div className="text-sm text-purple-700 font-semibold mb-1">Largest Expense</div>
                        <div className="text-2xl font-bold text-purple-900">
                          ${filteredStats.largestExpense.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div className="text-xs text-purple-600 mt-1">Single transaction</div>
                      </div>
                      <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4">
                        <div className="text-sm text-orange-700 font-semibold mb-1">Categories</div>
                        <div className="text-2xl font-bold text-orange-900">
                          {filteredCategoryData.length}
                        </div>
                        <div className="text-xs text-orange-600 mt-1">Expense types</div>
                      </div>
                    </div>

                  {/* Charts Row */}
                  <div className="grid lg:grid-cols-2 gap-6">
                    {/* Daily Spending Trend */}
                    <div className="bg-white rounded-xl p-6 shadow-lg">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Daily Spending Trend</h3>
                      {dailySpending.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <LineChart data={dailySpending}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis 
                              dataKey="date" 
                              tick={{ fontSize: 11 }}
                              tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip 
                              formatter={(value) => [`$${value}`, 'Spent']}
                              labelFormatter={(label) => new Date(label).toLocaleDateString()}
                            />
                            <Line type="monotone" dataKey="amount" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', r: 3 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-64 flex items-center justify-center text-gray-400">
                          <div className="text-center">
                            <div className="text-4xl mb-2">📊</div>
                            <p>No spending data for this period</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Category Breakdown Pie Chart */}
                    <div className="bg-white rounded-xl p-6 shadow-lg">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">🎯 Spending by Category</h3>
                      {filteredCategoryData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <PieChart>
                            <Pie
                              data={filteredCategoryData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={(entry: any) => `${entry.name}: ${((entry.percent || 0) * 100).toFixed(0)}%`}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {filteredCategoryData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => `$${Number(value).toFixed(2)}`} />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-64 flex items-center justify-center text-gray-400">
                          <div className="text-center">
                            <div className="text-4xl mb-2">🎯</div>
                            <p>No category data for this period</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Top Merchants */}
                  {topMerchants.length > 0 && (
                    <div className="bg-white rounded-xl p-6 shadow-lg">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">🏪 Top Merchants & Vendors</h3>
                      <div className="space-y-3">
                        {topMerchants.map((merchant, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                                index === 0 ? 'bg-yellow-500' :
                                index === 1 ? 'bg-gray-400' :
                                index === 2 ? 'bg-orange-600' :
                                'bg-blue-500'
                              }`}>
                                {index + 1}
                              </div>
                              <span className="font-semibold text-gray-900">{merchant.name.substring(0, 40)}</span>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-gray-900">${merchant.amount.toFixed(2)}</div>
                              <div className="text-xs text-gray-500">
                                {((merchant.amount / filteredStats.totalExpenses) * 100).toFixed(1)}% of spending
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* EXPENSES TAB */}
          {activeTab === 'expenses' && (
            <div className="space-y-6">
              {/* Add Expense Form */}
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  {editingExpense ? 'Edit Expense' : 'Add New Expense'}
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Date</label>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Amount</label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">$</span>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.amount}
                          onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                          className="w-full pl-9 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="0.00"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value as ExpenseCategory })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="e.g., Adobe Creative Cloud subscription"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Business Purpose <span className="text-gray-500 font-normal">(Recommended for audit defense)</span>
                    </label>
                    <textarea
                      value={formData.businessPurpose}
                      onChange={(e) => setFormData({ ...formData, businessPurpose: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Explain how this expense relates to your business..."
                    />
                  </div>

                  {/* Recurring Expense Options */}
                  {!editingExpense && (
                    <div className="border-2 border-blue-200 bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <input
                          type="checkbox"
                          id="recurring"
                          checked={isRecurring}
                          onChange={(e) => setIsRecurring(e.target.checked)}
                          className="w-5 h-5 text-blue-600"
                        />
                        <label htmlFor="recurring" className="font-bold text-gray-900 cursor-pointer">
                          🔄 Recurring Expense (Auto-generate multiple entries)
                        </label>
                      </div>
                      
                      {isRecurring && (
                        <div className="grid md:grid-cols-2 gap-4 mt-3">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Frequency</label>
                            <select
                              value={recurringFrequency}
                              onChange={(e) => setRecurringFrequency(e.target.value as 'monthly' | 'annual')}
                              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="monthly">Monthly</option>
                              <option value="annual">Annual</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              {recurringFrequency === 'monthly' ? 'Number of Months' : 'Number of Years'}
                            </label>
                            <input
                              type="number"
                              min="1"
                              max={recurringFrequency === 'monthly' ? '12' : '5'}
                              value={recurringMonths}
                              onChange={(e) => setRecurringMonths(parseInt(e.target.value))}
                              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div className="md:col-span-2 bg-white rounded-lg p-3 text-sm">
                            <strong>📅 Preview:</strong> This will create {recurringMonths} {recurringFrequency} expense{recurringMonths > 1 ? 's' : ''} 
                            {formData.amount && ` totaling $${(parseFloat(formData.amount) * recurringMonths).toFixed(2)}`}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg font-bold hover:bg-primary-700 transition-colors"
                    >
                      {editingExpense ? 'Update Expense' : 'Add Expense'}
                    </button>
                    {editingExpense && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditingExpense(null);
                          setFormData({
                            date: new Date().toISOString().split('T')[0],
                            amount: '',
                            category: 'office-expense',
                            description: '',
                            businessPurpose: '',
                          });
                        }}
                        className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-bold hover:bg-gray-300 transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>

              {/* Expense List */}
              <div className="bg-white rounded-xl p-4 md:p-6 shadow-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 md:mb-6">
                  <h2 className="text-lg md:text-xl font-bold text-gray-900">All Expenses ({expenses.length})</h2>
                  
                  {/* Export Buttons */}
                  {expenses.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={exportToCSV}
                        className="flex items-center gap-1 md:gap-2 px-3 md:px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors text-xs md:text-sm whitespace-nowrap"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="hidden sm:inline">Export </span>CSV
                      </button>
                      <button
                        onClick={exportScheduleC}
                        className="flex items-center gap-1 md:gap-2 px-3 md:px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors text-xs md:text-sm whitespace-nowrap"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Schedule C
                      </button>
                      <button
                        onClick={exportToPDF}
                        className={`flex items-center gap-1 md:gap-2 px-3 md:px-4 py-2 rounded-lg font-semibold transition-colors text-xs md:text-sm whitespace-nowrap ${
                          isPremium 
                            ? 'bg-red-600 text-white hover:bg-red-700' 
                            : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        {isPremium ? 'PDF' : 'PDF'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Search and Filter */}
                <div className="flex flex-col sm:flex-row gap-3 mb-4 md:mb-6">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search expenses..."
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm md:text-base"
                  />
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value as ExpenseCategory | 'all')}
                    className="w-full sm:w-auto px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm md:text-base"
                  >
                    <option value="all">All Categories</option>
                    {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                {filteredExpenses.length > 0 ? (
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <div className="inline-block min-w-full align-middle">
                    <table className="w-full min-w-[700px]">
                      <thead className="bg-gray-50 border-b-2 border-gray-200">
                        <tr>
                          <th className="px-3 md:px-4 py-2 md:py-3 text-left text-xs font-bold text-gray-600 uppercase">Date</th>
                          <th className="px-3 md:px-4 py-2 md:py-3 text-left text-xs font-bold text-gray-600 uppercase">Category</th>
                          <th className="px-3 md:px-4 py-2 md:py-3 text-left text-xs font-bold text-gray-600 uppercase">Description</th>
                          <th className="px-3 md:px-4 py-2 md:py-3 text-right text-xs font-bold text-gray-600 uppercase">Amount</th>
                          {isPremium && <th className="px-3 md:px-4 py-2 md:py-3 text-center text-xs font-bold text-gray-600 uppercase">Receipt</th>}
                          <th className="px-3 md:px-4 py-2 md:py-3 text-right text-xs font-bold text-gray-600 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredExpenses.map((expense) => (
                          <tr key={expense.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-3 md:px-4 py-3 md:py-4 text-xs md:text-sm text-gray-900 font-medium whitespace-nowrap">{expense.date}</td>
                            <td className="px-3 md:px-4 py-3 md:py-4 text-sm">
                              <span className="px-2 md:px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold whitespace-nowrap">
                                {CATEGORY_LABELS[expense.category]}
                              </span>
                            </td>
                            <td className="px-3 md:px-4 py-3 md:py-4 text-xs md:text-sm text-gray-900">
                              <div className="max-w-[200px] md:max-w-none truncate md:whitespace-normal">{expense.description}</div>
                              {expense.businessPurpose && (
                                <div className="text-xs text-gray-500 mt-1 hidden md:block">
                                  {expense.businessPurpose.substring(0, 60)}...
                                </div>
                              )}
                            </td>
                            <td className="px-3 md:px-4 py-3 md:py-4 text-xs md:text-sm text-gray-900 text-right font-bold whitespace-nowrap">
                              ${expense.amount.toFixed(2)}
                            </td>
                            {isPremium && (
                              <td className="px-3 md:px-4 py-3 md:py-4 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  <input
                                    type="file"
                                    id={`receipt-${expense.id}`}
                                    accept="image/*,application/pdf"
                                    className="hidden"
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (file && currentUser?.email) {
                                        try {
                                          setUploadingReceipt(expense.id);
                                          await uploadReceipt(file, expense.id, currentUser.email);
                                          // Reload receipts
                                          const receipts = getReceiptsByExpenseId(expense.id, currentUser.email);
                                          setReceiptsByExpense(prev => ({ ...prev, [expense.id]: receipts }));
                                          setUploadingReceipt(null);
                                        } catch (error) {
                                          alert(error instanceof Error ? error.message : 'Failed to upload receipt');
                                          setUploadingReceipt(null);
                                        }
                                      }
                                    }}
                                  />
                                  <label
                                    htmlFor={`receipt-${expense.id}`}
                                    className="cursor-pointer text-blue-600 hover:text-blue-800"
                                    title="Upload receipt"
                                  >
                                    {uploadingReceipt === expense.id ? '⏳' : 
                                     receiptsByExpense[expense.id]?.length > 0 ? `📎 (${receiptsByExpense[expense.id].length})` : '📎'}
                                  </label>
                                  {receiptsByExpense[expense.id]?.length > 0 && (
                                    <button
                                      onClick={() => {
                                        const receipt = receiptsByExpense[expense.id][0];
                                        window.open(receipt.dataUrl, '_blank');
                                      }}
                                      className="text-green-600 hover:text-green-800 text-sm"
                                      title="View receipt"
                                    >
                                      👁️
                                    </button>
                                  )}
                                </div>
                              </td>
                            )}
                            <td className="px-4 py-4 text-right">
                              <button
                                onClick={() => handleEdit(expense)}
                                className="text-primary-600 hover:text-primary-800 font-semibold text-sm mr-3"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(expense.id)}
                                className="text-red-600 hover:text-red-800 font-semibold text-sm"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <div className="text-6xl mb-4">📋</div>
                    <p className="text-lg">No expenses found</p>
                    <p className="text-sm">Add your first expense above</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* REPORTS TAB */}
          {activeTab === 'reports' && (
            <div className="space-y-6">
              
              {/* Audit Risk Report */}
              <div className="bg-white rounded-xl p-4 md:p-8 shadow-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 md:mb-6">
                  <div className="flex-1">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900">🎯 IRS Audit Risk Analysis</h2>
                    <p className="text-sm md:text-base text-gray-600 mt-1">Comprehensive risk assessment based on IRS audit triggers</p>
                  </div>
                  <div className={`text-4xl md:text-5xl font-bold text-center sm:text-right ${
                    riskLevel.label === 'Low' ? 'text-green-600' :
                    riskLevel.label === 'Medium' ? 'text-yellow-600' :
                    riskLevel.label === 'High' ? 'text-orange-600' :
                    'text-red-600'
                  }`}>
                    {stats.auditRiskScore}
                  </div>
                </div>

                {isPremium ? (
                  <div className="space-y-6">
                    {/* Risk Factors Breakdown */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="border-2 border-gray-200 rounded-lg p-3 md:p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs md:text-sm font-semibold text-gray-600">TOTAL EXPENSES</span>
                          <span className="text-base md:text-lg font-bold text-gray-900">
                            ${stats.totalExpenses.toLocaleString()}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {stats.totalExpenses / income > 0.52 
                            ? '⚠️ Expenses exceed 52% of income'
                            : '✓ Expense ratio within safe range'}
                        </div>
                      </div>

                      <div className="border-2 border-gray-200 rounded-lg p-3 md:p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs md:text-sm font-semibold text-gray-600">RISK SCORE</span>
                          <span className={`text-base md:text-lg font-bold ${
                            riskLevel.label === 'Low' ? 'text-green-600' :
                            riskLevel.label === 'Medium' ? 'text-yellow-600' :
                            riskLevel.label === 'High' ? 'text-orange-600' : 'text-red-600'
                          }`}>
                            {stats.auditRiskScore}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {riskLevel.description}
                        </div>
                      </div>

                      <div className="border-2 border-gray-200 rounded-lg p-3 md:p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs md:text-sm font-semibold text-gray-600">TOTAL INCOME</span>
                          <span className="text-base md:text-lg font-bold text-gray-900">
                            ${income.toLocaleString()}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          ✓ Reported gross income for {currentTaxYear}
                        </div>
                      </div>

                      <div className="border-2 border-gray-200 rounded-lg p-3 md:p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs md:text-sm font-semibold text-gray-600">EXPENSE COUNT</span>
                          <span className="text-base md:text-lg font-bold text-gray-900">
                            {expenses.length}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          ✓ Tracked expenses with receipts
                        </div>
                      </div>
                    </div>

                    {/* Risk Level Explanation */}
                    <div className={`rounded-xl p-4 md:p-6 ${
                      riskLevel.label === 'Low' ? 'bg-green-50 border-2 border-green-200' :
                      riskLevel.label === 'Medium' ? 'bg-yellow-50 border-2 border-yellow-200' :
                      riskLevel.label === 'High' ? 'bg-orange-50 border-2 border-orange-200' :
                      'bg-red-50 border-2 border-red-200'
                    }`}>
                      <h3 className="font-bold text-base md:text-lg mb-2">
                        {riskLevel.label === 'Low' && '✓ Low Risk Profile'}
                        {riskLevel.label === 'Medium' && '⚡ Medium Risk - Review Recommended'}
                        {riskLevel.label === 'High' && '⚠️ High Risk - Action Required'}
                        {riskLevel.label === 'Very High' && '🚨 Very High Risk - Immediate Attention'}
                      </h3>
                      <p className="text-sm text-gray-700">
                        {riskLevel.description}
                      </p>
                    </div>

                    {/* Risk Factors List */}
                    {stats.riskFactors && stats.riskFactors.length > 0 && (
                      <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
                        <h3 className="font-bold text-lg text-red-900 mb-3">🚩 IRS Audit Risk Factors</h3>
                        <ul className="space-y-2 text-sm text-red-800">
                          {stats.riskFactors.map((factor, idx) => (
                            <li key={idx}>• <strong>{factor.category}:</strong> {factor.issue} - {factor.recommendation}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">🔒</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Full Audit Risk Report - Premium Feature</h3>
                    <p className="text-gray-600 mb-6">Unlock detailed risk analysis, red flag warnings, and compliance recommendations</p>
                    <button
                      onClick={() => setShowUpgradeModal(true)}
                      className="px-8 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg font-bold hover:shadow-lg transition-shadow"
                    >
                      ⚡ Start 14-Day FREE Trial
                    </button>
                  </div>
                )}
              </div>

              {/* Tax Deduction Summary */}
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">💰 Tax Deduction Summary by Category</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categoryData.map((cat) => (
                    <div key={cat.category} className="border-2 border-gray-200 rounded-lg p-4 hover:border-blue-400 transition-colors">
                      <div className="text-sm font-semibold text-gray-600 mb-1">{cat.name}</div>
                      <div className="text-2xl font-bold text-gray-900">${cat.value.toFixed(2)}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {((cat.value / stats.totalExpenses) * 100).toFixed(1)}% of total
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-gray-900">Total Deductible Expenses</span>
                    <span className="text-2xl font-bold text-blue-600">${stats.totalExpenses.toFixed(2)}</span>
                  </div>
                  <div className="text-sm text-gray-600 mt-2">
                    Estimated Tax Savings (30% bracket): <strong className="text-green-600">${(stats.totalExpenses * 0.30).toFixed(2)}</strong>
                  </div>
                </div>
              </div>

              {/* Monthly Trend Analysis */}
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">📈 Expense Trend Analysis</h2>
                {monthlySpending.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={monthlySpending}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip formatter={(value) => `$${value}`} />
                        <Legend />
                        <Line type="monotone" dataKey="amount" stroke="#3b82f6" strokeWidth={3} name="Monthly Expenses" />
                      </LineChart>
                    </ResponsiveContainer>
                    <div className="grid md:grid-cols-3 gap-4 mt-6">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-sm text-gray-600 mb-1">Average per Month</div>
                        <div className="text-xl font-bold text-gray-900">
                          ${(monthlySpending.reduce((sum, m) => sum + m.amount, 0) / monthlySpending.length).toFixed(2)}
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-sm text-gray-600 mb-1">Highest Month</div>
                        <div className="text-xl font-bold text-gray-900">
                          ${Math.max(...monthlySpending.map(m => m.amount)).toFixed(2)}
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-sm text-gray-600 mb-1">Lowest Month</div>
                        <div className="text-xl font-bold text-gray-900">
                          ${Math.min(...monthlySpending.map(m => m.amount)).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <p>Add expenses to see trend analysis</p>
                  </div>
                )}
              </div>

              {/* Year-End Projection */}
              <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl p-8 shadow-lg text-white">
                <h2 className="text-2xl font-bold mb-4">🎯 {currentTaxYear} Year-End Tax Projection</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-sm opacity-90 mb-1">Projected Annual Expenses</div>
                    <div className="text-3xl font-bold">
                      ${monthlySpending.length > 0 
                        ? ((monthlySpending.reduce((sum, m) => sum + m.amount, 0) / monthlySpending.length) * 12).toFixed(2)
                        : '0.00'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm opacity-90 mb-1">Estimated Tax Savings (30%)</div>
                    <div className="text-3xl font-bold">
                      ${monthlySpending.length > 0 
                        ? (((monthlySpending.reduce((sum, m) => sum + m.amount, 0) / monthlySpending.length) * 12) * 0.30).toFixed(2)
                        : '0.00'}
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-sm opacity-90">
                  *Based on average monthly spending. Actual tax savings depend on your tax bracket and deduction eligibility.
                </div>
              </div>

            </div>
          )}

          {/* TAX ESTIMATES TAB */}
          {activeTab === 'tax-estimates' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-6 md:p-8 shadow-lg text-white">
                <h1 className="text-2xl md:text-3xl font-bold mb-2">💰 Quarterly Tax Estimates</h1>
                <p className="text-sm md:text-base opacity-90">IRS requires estimated tax payments throughout the year. Calculate your quarterly obligations below.</p>
              </div>

              {/* Quarterly Payment Schedule */}
              <div className="bg-white rounded-xl p-4 md:p-8 shadow-lg">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">📅 {currentTaxYear} Payment Schedule</h2>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
                  {calculateQuarterlyEstimates(income, filteredExpenses.reduce((sum, e) => sum + e.amount, 0), currentTaxYear).map((estimate) => (
                    <div
                      key={estimate.quarter}
                      className="border-2 border-gray-200 rounded-lg p-4 md:p-6 hover:border-blue-400 transition-colors"
                    >
                      <div className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{estimate.quarter}</div>
                      <div className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4">{estimate.period}</div>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-2 md:p-3 mb-2 md:mb-3">
                        <div className="text-xs text-red-600 font-semibold mb-1">DUE DATE</div>
                        <div className="text-base md:text-lg font-bold text-red-600">{estimate.dueDate}</div>
                      </div>
                      <div className="space-y-2 text-xs md:text-sm mb-3 md:mb-4">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Income:</span>
                          <span className="font-semibold">${estimate.income.toFixed(0)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Expenses:</span>
                          <span className="font-semibold">-${estimate.expenses.toFixed(0)}</span>
                        </div>
                        <div className="flex justify-between border-t pt-2">
                          <span className="text-gray-600">Net Income:</span>
                          <span className="font-bold">${estimate.netIncome.toFixed(0)}</span>
                        </div>
                      </div>
                      <div className="border-t pt-3">
                        <div className="text-xs text-gray-600 mb-1">ESTIMATED TAX DUE</div>
                        <div className="text-2xl font-bold text-green-600">${estimate.totalTaxDue.toFixed(2)}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          SE Tax: ${estimate.selfEmploymentTax.toFixed(2)}<br />
                          Income Tax: ${estimate.incomeTax.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Annual Summary */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6">
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Total Annual Tax</div>
                      <div className="text-3xl font-bold text-gray-900">
                        ${calculateQuarterlyEstimates(income, filteredExpenses.reduce((sum, e) => sum + e.amount, 0), currentTaxYear)
                          .reduce((sum, est) => sum + est.totalTaxDue, 0).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Next Payment Due</div>
                      <div className="text-2xl font-bold text-red-600">
                        {(() => {
                          const next = getNextQuarterDue();
                          return next ? `${next.quarter} - ${next.dueDate}` : 'N/A';
                        })()}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Days Until Due</div>
                      <div className="text-2xl font-bold text-orange-600">
                        {(() => {
                          const next = getNextQuarterDue();
                          return next ? `${next.daysUntil} days` : 'N/A';
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tax Payment Guide */}
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">📋 How to Pay Estimated Taxes</h2>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">1️⃣</div>
                    <div>
                      <div className="font-bold mb-1">Pay Online (Fastest)</div>
                      <div className="text-sm">Visit <a href="https://www.irs.gov/payments" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">irs.gov/payments</a> and use IRS Direct Pay (free) or pay by card (fee applies)</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">2️⃣</div>
                    <div>
                      <div className="font-bold mb-1">Mail Form 1040-ES</div>
                      <div className="text-sm">Download payment vouchers from IRS website and mail with check to the address for your state</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">3️⃣</div>
                    <div>
                      <div className="font-bold mb-1">Use EFTPS</div>
                      <div className="text-sm">Enroll at <a href="https://www.eftps.gov" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">eftps.gov</a> for free electronic payment system</div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                  <div className="font-bold text-yellow-900 mb-2">⚠️ Important Notes</div>
                  <ul className="text-sm text-yellow-900 space-y-1">
                    <li>• Estimates are based on simplified calculations - consult a tax professional for accuracy</li>
                    <li>• You may owe penalties if you underpay by more than 10%</li>
                    <li>• Consider safe harbor rule: Pay 100% of last year's tax (110% if income &gt; $150k)</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* MILEAGE TAB */}
          {activeTab === 'mileage' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl p-8 shadow-lg text-white">
                <h1 className="text-3xl font-bold mb-2">🚗 Mileage Tracker</h1>
                <p className="opacity-90">Track business miles at IRS standard rate: ${getCurrentMileageRate(currentTaxYear)} per mile ({currentTaxYear})</p>
                <div className="mt-4 flex gap-4">
                  <div className="bg-white/20 rounded-lg px-4 py-2">
                    <div className="text-sm opacity-90">Total Miles</div>
                    <div className="text-2xl font-bold">{getTotalMiles(currentUser?.email || '')}</div>
                  </div>
                  <div className="bg-white/20 rounded-lg px-4 py-2">
                    <div className="text-sm opacity-90">Total Deduction</div>
                    <div className="text-2xl font-bold">${getTotalMileageDeduction(currentUser?.email || '').toFixed(2)}</div>
                  </div>
                </div>
              </div>

              {/* Add Mileage Form */}
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">➕ Log Business Miles</h2>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  if (!currentUser?.email || !mileageForm.miles) return;
                  
                  const miles = parseFloat(mileageForm.miles);
                  const entry: MileageEntry = {
                    id: `mileage_${Date.now()}`,
                    date: mileageForm.date,
                    startLocation: mileageForm.startLocation,
                    endLocation: mileageForm.endLocation,
                    miles,
                    purpose: mileageForm.purpose,
                    rate: getCurrentMileageRate(currentTaxYear),
                    deduction: calculateMileageDeduction(miles, currentTaxYear),
                    userId: currentUser.email
                  };
                  
                  storeMileageEntry(entry);
                  setMileageEntries([...getMileageEntries(currentUser.email)]);
                  
                  // Also add as vehicle expense
                  const newExpense: Expense = {
                    id: `exp_${Date.now()}`,
                    date: mileageForm.date,
                    amount: entry.deduction,
                    category: 'vehicle',
                    description: `Mileage: ${mileageForm.startLocation} → ${mileageForm.endLocation} (${miles} mi)`,
                    businessPurpose: mileageForm.purpose,
                    createdAt: new Date().toISOString(),
                    userId: currentUser.email
                  };
                  setExpenses([...expenses, newExpense]);
                  
                  setMileageForm({
                    date: new Date().toISOString().split('T')[0],
                    startLocation: '',
                    endLocation: '',
                    miles: '',
                    purpose: ''
                  });
                }} className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Date</label>
                    <input
                      type="date"
                      value={mileageForm.date}
                      onChange={(e) => setMileageForm({...mileageForm, date: e.target.value})}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Miles Driven</label>
                    <input
                      type="number"
                      step="0.1"
                      value={mileageForm.miles}
                      onChange={(e) => setMileageForm({...mileageForm, miles: e.target.value})}
                      placeholder="0.0"
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">From</label>
                    <input
                      type="text"
                      value={mileageForm.startLocation}
                      onChange={(e) => setMileageForm({...mileageForm, startLocation: e.target.value})}
                      placeholder="Starting location"
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">To</label>
                    <input
                      type="text"
                      value={mileageForm.endLocation}
                      onChange={(e) => setMileageForm({...mileageForm, endLocation: e.target.value})}
                      placeholder="Destination"
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Business Purpose</label>
                    <input
                      type="text"
                      value={mileageForm.purpose}
                      onChange={(e) => setMileageForm({...mileageForm, purpose: e.target.value})}
                      placeholder="Client meeting, site visit, etc."
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    {mileageForm.miles && (
                      <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mb-4">
                        <div className="text-sm text-gray-600">Deduction for {mileageForm.miles} miles:</div>
                        <div className="text-2xl font-bold text-green-600">
                          ${calculateMileageDeduction(parseFloat(mileageForm.miles), currentTaxYear).toFixed(2)}
                        </div>
                      </div>
                    )}
                    <button
                      type="submit"
                      className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg font-bold hover:shadow-lg transition-shadow"
                    >
                      📝 Log Mileage
                    </button>
                  </div>
                </form>
              </div>

              {/* Mileage History */}
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">📋 Mileage Log</h2>
                {getMileageEntries(currentUser?.email || '').length > 0 ? (
                  <div className="space-y-3">
                    {getMileageEntries(currentUser?.email || '')
                      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                      .map((entry) => (
                      <div key={entry.id} className="border-2 border-gray-200 rounded-lg p-4 hover:border-blue-400 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="text-sm font-semibold text-gray-900">
                                {new Date(entry.date).toLocaleDateString()}
                              </div>
                              <div className="text-lg font-bold text-blue-600">{entry.miles} mi</div>
                              <div className="text-lg font-bold text-green-600">${entry.deduction.toFixed(2)}</div>
                            </div>
                            <div className="text-sm text-gray-600 mb-1">
                              <span className="font-semibold">{entry.startLocation}</span> → <span className="font-semibold">{entry.endLocation}</span>
                            </div>
                            <div className="text-sm text-gray-500">{entry.purpose}</div>
                          </div>
                          <button
                            onClick={() => {
                              if (currentUser?.email) {
                                deleteMileageEntry(entry.id, currentUser.email);
                                setMileageEntries(getMileageEntries(currentUser.email));
                              }
                            }}
                            className="text-red-500 hover:text-red-700 text-xl ml-4"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <div className="text-6xl mb-4">🚗</div>
                    <p>No mileage entries yet. Add your first business trip above!</p>
                  </div>
                )}
              </div>

              {/* IRS Mileage Info */}
              <div className="bg-white rounded-xl p-4 md:p-8 shadow-lg">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">📚 IRS Standard Mileage Rates</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div className="border-2 border-gray-200 rounded-lg p-3 md:p-4">
                    <div className="text-xs md:text-sm text-gray-600 mb-1">{currentTaxYear} Rate</div>
                    <div className="text-xl md:text-2xl font-bold text-gray-900">{getCurrentMileageRate(currentTaxYear)}¢/mile</div>
                  </div>
                  <div className="border-2 border-gray-200 rounded-lg p-3 md:p-4">
                    <div className="text-xs md:text-sm text-gray-600 mb-1">2023 Rate</div>
                    <div className="text-xl md:text-2xl font-bold text-gray-500">65.5¢/mile</div>
                  </div>
                  <div className="border-2 border-gray-200 rounded-lg p-3 md:p-4">
                    <div className="text-xs md:text-sm text-gray-600 mb-1">2022 Rate</div>
                    <div className="text-xl md:text-2xl font-bold text-gray-400">62.5¢/mile</div>
                  </div>
                </div>
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                  <div className="font-bold text-blue-900 mb-2">✅ What Qualifies as Business Mileage?</div>
                  <ul className="text-sm text-blue-900 space-y-1">
                    <li>• Traveling between job sites or client meetings</li>
                    <li>• Driving to business errands (bank, post office, supplies)</li>
                    <li>• Attending business conferences, seminars, or networking events</li>
                    <li>• Meeting with advisors (accountant, lawyer, etc.)</li>
                  </ul>
                  <div className="mt-3 pt-3 border-t border-blue-300">
                    <div className="font-bold text-blue-900 mb-2">❌ What Doesn't Qualify?</div>
                    <ul className="text-sm text-blue-900 space-y-1">
                      <li>• Commuting between home and regular workplace</li>
                      <li>• Personal errands or vacation travel</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Invoices Tab */}
          {activeTab === 'invoices' && (
            <div className="space-y-6">
              {/* Invoice Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 border-l-4 border-blue-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-xs md:text-sm font-medium">Total Revenue</p>
                      <p className="text-2xl md:text-3xl font-bold text-gray-900 mt-1 md:mt-2">
                        ${invoiceStats.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="bg-blue-100 rounded-full p-2 md:p-3">
                      <span className="text-2xl md:text-3xl">💰</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 border-l-4 border-green-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-xs md:text-sm font-medium">Paid Invoices</p>
                      <p className="text-2xl md:text-3xl font-bold text-gray-900 mt-1 md:mt-2">{invoiceStats.paidInvoices}</p>
                      <p className="text-xs md:text-sm text-green-600 mt-1">of {invoiceStats.totalInvoices} total</p>
                    </div>
                    <div className="bg-green-100 rounded-full p-2 md:p-3">
                      <span className="text-2xl md:text-3xl">✅</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 border-l-4 border-yellow-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-xs md:text-sm font-medium">Pending</p>
                      <p className="text-2xl md:text-3xl font-bold text-gray-900 mt-1 md:mt-2">{invoiceStats.pendingInvoices}</p>
                      <p className="text-xs md:text-sm text-yellow-600 mt-1">awaiting payment</p>
                    </div>
                    <div className="bg-yellow-100 rounded-full p-2 md:p-3">
                      <span className="text-2xl md:text-3xl">⏳</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 border-l-4 border-red-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-xs md:text-sm font-medium">Overdue</p>
                      <p className="text-2xl md:text-3xl font-bold text-gray-900 mt-1 md:mt-2">{invoiceStats.overdueInvoices}</p>
                      <p className="text-xs md:text-sm text-red-600 mt-1">needs attention</p>
                    </div>
                    <div className="bg-red-100 rounded-full p-2 md:p-3">
                      <span className="text-2xl md:text-3xl">⚠️</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stripe Connect Status */}
              {!stripeAccountStatus.connected ? (
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg p-6 md:p-8 text-white">
                  <div className="text-center">
                    <div className="text-4xl md:text-5xl mb-3 md:mb-4">💳</div>
                    <h3 className="text-xl md:text-2xl font-bold mb-2 md:mb-3">Connect Your Stripe Account</h3>
                    <p className="text-sm md:text-base text-blue-100 mb-4 md:mb-6 max-w-2xl mx-auto">
                      To send invoices and receive payments, you need to connect your Stripe account. 
                      Payments go directly to your account - we never touch your money!
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center mb-4 md:mb-6">
                      <div className="flex items-center gap-2 text-sm md:text-base text-blue-100">
                        <span>✓</span> Secure payments
                      </div>
                      <div className="flex items-center gap-2 text-sm md:text-base text-blue-100">
                        <span>✓</span> Direct deposits
                      </div>
                      <div className="flex items-center gap-2 text-sm md:text-base text-blue-100">
                        <span>✓</span> Professional invoices
                      </div>
                    </div>
                    <button
                      onClick={connectStripeAccount}
                      disabled={loadingStripeConnect}
                      className="bg-white text-blue-600 px-6 md:px-8 py-3 md:py-4 rounded-lg font-bold text-base md:text-lg hover:bg-blue-50 transition-colors shadow-xl disabled:opacity-50 w-full sm:w-auto"
                    >
                      {loadingStripeConnect ? 'Connecting...' : '🔗 Connect Stripe Account'}
                    </button>
                    <p className="text-xs text-blue-200 mt-3 md:mt-4">
                      Free to connect • No monthly fees • Only pay when you get paid
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Stripe Connected - Show Dashboard Link */}
                  <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">✅</span>
                        <div>
                          <p className="font-semibold text-green-900">Stripe Connected</p>
                          <p className="text-sm text-green-700">Ready to send invoices and receive payments</p>
                        </div>
                      </div>
                      <button
                        onClick={openStripeDashboard}
                        className="px-4 py-2 border-2 border-green-600 text-green-700 rounded-lg font-semibold hover:bg-green-100 transition-colors"
                      >
                        View Dashboard
                      </button>
                    </div>
                  </div>

                  {/* Create Invoice Button */}
                  <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg p-4 md:p-6 text-white">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-lg md:text-xl font-bold mb-1 md:mb-2">Create Professional Invoices</h3>
                        <p className="text-sm md:text-base text-blue-100">Send invoices with Stripe payment links and get paid faster</p>
                      </div>
                      <button
                        onClick={() => setShowCreateInvoiceModal(true)}
                        className="bg-white text-blue-600 px-4 md:px-6 py-2 md:py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap w-full sm:w-auto text-center"
                      >
                        + Create Invoice
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Invoices List */}
              {stripeAccountStatus.connected && (
              <div className="bg-white rounded-xl shadow-lg overflow-hidden mt-6">
                <div className="p-4 md:p-6 border-b border-gray-200">
                  <div className="flex flex-col gap-3 md:gap-4">
                    <input
                      type="text"
                      placeholder="Search invoices..."
                      value={invoiceSearchTerm}
                      onChange={(e) => setInvoiceSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base"
                    />
                    <div className="flex flex-col sm:flex-row gap-3">
                    <select
                      value={invoiceDateFilter}
                      onChange={(e) => setInvoiceDateFilter(e.target.value as any)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base"
                    >
                      <option value="all">All Time</option>
                      <option value="30days">Last 30 Days</option>
                      <option value="3months">Last 3 Months</option>
                      <option value="6months">Last 6 Months</option>
                      <option value="year">Last Year</option>
                    </select>
                    <select
                      value={invoiceFilterStatus}
                      onChange={(e) => setInvoiceFilterStatus(e.target.value as any)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base"
                    >
                      <option value="all">All Status</option>
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="viewed">Viewed</option>
                      <option value="paid">Paid</option>
                      <option value="overdue">Overdue</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    </div>
                  </div>
                </div>

                {invoices.length === 0 ? (
                  <div className="text-center py-16">
                    <span className="text-6xl mb-4 block">📄</span>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No invoices yet</h3>
                    <p className="text-gray-600 mb-6">Create your first invoice to start getting paid</p>
                    <button
                      onClick={() => setShowCreateInvoiceModal(true)}
                      className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                    >
                      Create Your First Invoice
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <div className="inline-block min-w-full align-middle">
                      <table className="w-full min-w-[800px]">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Invoice</th>
                            <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Client</th>
                            <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                            <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Due Date</th>
                            <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                            <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                        {filterInvoicesByDate(invoices)
                          .filter(invoice => {
                            const matchesStatus = invoiceFilterStatus === 'all' || invoice.status === invoiceFilterStatus;
                            const matchesSearch = invoice.clientName.toLowerCase().includes(invoiceSearchTerm.toLowerCase()) ||
                                                invoice.invoiceNumber.toLowerCase().includes(invoiceSearchTerm.toLowerCase());
                            return matchesStatus && matchesSearch;
                          })
                          .map(invoice => (
                            <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                                <div className="font-semibold text-sm md:text-base text-gray-900">{invoice.invoiceNumber}</div>
                                <div className="text-xs md:text-sm text-gray-500">{new Date(invoice.issueDate).toLocaleDateString()}</div>
                              </td>
                              <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                                <div className="text-sm md:text-base text-gray-900">{invoice.clientName}</div>
                                <div className="text-xs md:text-sm text-gray-500 truncate max-w-[150px]">{invoice.clientEmail}</div>
                              </td>
                              <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap font-semibold text-sm md:text-base text-gray-900">
                                ${invoice.total.toFixed(2)}
                              </td>
                              <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-600">
                                {new Date(invoice.dueDate).toLocaleDateString()}
                              </td>
                              <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                                  invoice.status === 'paid' ? 'bg-green-100 text-green-800 border-green-200' :
                                  invoice.status === 'sent' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                                  invoice.status === 'viewed' ? 'bg-purple-100 text-purple-800 border-purple-200' :
                                  invoice.status === 'overdue' ? 'bg-red-100 text-red-800 border-red-200' :
                                  invoice.status === 'draft' ? 'bg-gray-100 text-gray-800 border-gray-200' :
                                  'bg-slate-100 text-slate-800 border-slate-200'
                                }`}>
                                  {invoice.status.toUpperCase()}
                                </span>
                              </td>
                              <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm">
                                <div className="flex items-center gap-1 md:gap-2">
                                  <button
                                    onClick={async () => {
                                      try {
                                        const response = await fetch('/.netlify/functions/send-invoice', {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ invoice, stripeAccountId }),
                                        });
                                        
                                        if (response.ok) {
                                          const data = await response.json();
                                          const subject = encodeURIComponent(`Invoice ${invoice.invoiceNumber} from ${invoice.businessName}`);
                                          const body = encodeURIComponent(
                                            `Hello ${invoice.clientName},\n\n` +
                                            `Please find your invoice below:\n\n` +
                                            `Invoice Number: ${invoice.invoiceNumber}\n` +
                                            `Amount Due: $${invoice.total.toFixed(2)}\n` +
                                            `Due Date: ${new Date(invoice.dueDate).toLocaleDateString()}\n\n` +
                                            `Click here to view and pay your invoice:\n${data.paymentLink}\n\n` +
                                            `Thank you for your business!\n\n${invoice.businessName}`
                                          );
                                          
                                          // Open email client
                                          window.open(`mailto:${invoice.clientEmail}?subject=${subject}&body=${body}`, '_blank');
                                          
                                          // Show payment link
                                          prompt('Payment link created! Copy this link:', data.paymentLink);
                                        }
                                      } catch (error) {
                                        alert('Failed to create payment link');
                                      }
                                    }}
                                    className="text-blue-600 hover:text-blue-800 font-medium"
                                    title="Send invoice"
                                  >
                                    📧
                                  </button>
                                  <button
                                    onClick={() => handleEditInvoice(invoice)}
                                    className="text-gray-600 hover:text-gray-800 font-medium"
                                    title="Edit invoice"
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    onClick={() => handleDeleteInvoice(invoice.id)}
                                    className="text-red-600 hover:text-red-800 font-medium"
                                    title="Delete invoice"
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Create Invoice Modal */}
      {showCreateInvoiceModal && (
        <CreateInvoiceModal
          isOpen={showCreateInvoiceModal}
          onClose={() => {
            setShowCreateInvoiceModal(false);
            setEditingInvoice(null);
          }}
          onSave={(invoice) => {
            let updatedInvoices;
            
            if (editingInvoice) {
              // Update existing invoice
              updatedInvoices = invoices.map(inv => inv.id === invoice.id ? invoice : inv);
            } else {
              // Add new invoice
              updatedInvoices = [...invoices, invoice];
            }
            
            setInvoices(updatedInvoices);
            localStorage.setItem(getUserStorageKey('invoices', currentUser?.email), JSON.stringify(updatedInvoices));
            
            // Update stats
            const totalInvoices = updatedInvoices.length;
            const paidInvoices = updatedInvoices.filter(inv => inv.status === 'paid').length;
            const pendingInvoices = updatedInvoices.filter(inv => inv.status === 'sent' || inv.status === 'viewed').length;
            const overdueInvoices = updatedInvoices.filter(inv => inv.status === 'overdue').length;
            const totalRevenue = updatedInvoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.total, 0);
            const averageInvoiceValue = totalInvoices > 0 ? totalRevenue / paidInvoices || 0 : 0;
            
            setInvoiceStats({
              totalInvoices,
              totalRevenue,
              paidInvoices,
              pendingInvoices,
              overdueInvoices,
              averageInvoiceValue,
            });
            
            // Update client stats (only for new invoices)
            if (!editingInvoice) {
              const clientIndex = clients.findIndex(c => c.email === invoice.clientEmail);
              if (clientIndex >= 0) {
                const updatedClients = [...clients];
                updatedClients[clientIndex].totalInvoiced += invoice.total;
                updatedClients[clientIndex].invoiceCount += 1;
                setClients(updatedClients);
                localStorage.setItem(getUserStorageKey('clients', currentUser?.email), JSON.stringify(updatedClients));
              }
            }
            
            setEditingInvoice(null);
          }}
          clients={clients}
          onAddClient={(client) => {
            const updatedClients = [...clients, client];
            setClients(updatedClients);
            localStorage.setItem(getUserStorageKey('clients', currentUser?.email), JSON.stringify(updatedClients));
          }}
          stripeAccountId={stripeAccountId}
          currentUserId={currentUser?.email || ''}
          editingInvoice={editingInvoice}
        />
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 my-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Upgrade to Premium</h3>
            
            <div className="space-y-2 mb-5">
              {[
                'Unlimited Expenses',
                'Bank Integration & Auto-Import',
                'AI-Powered Categorization',
                'Quarterly Tax Estimates',
                'Full Audit Risk Analysis',
                'PDF & CSV Exports',
                'Mileage Tracking',
                'Receipt Upload'
              ].map((feature, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-1.5 flex-shrink-0"></div>
                  <div className="text-sm text-gray-700">{feature}</div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 mb-4 text-center border-2 border-yellow-200">
              <div className="text-lg font-bold text-green-600 mb-2">14-Day FREE Trial</div>
              <div className="text-4xl font-bold text-gray-900 mb-1">$5.99<span className="text-xl text-gray-600">/month</span></div>
              <div className="text-xs text-gray-600">After trial ends • Cancel anytime</div>
            </div>

            <div className="space-y-2">
              <button
                onClick={async () => {
                  setShowUpgradeModal(false);
                  await redirectToCheckout();
                }}
                className="w-full px-5 py-3 bg-gradient-to-r from-primary-600 to-blue-600 text-white rounded-lg font-bold hover:from-primary-700 hover:to-blue-700 transition-all shadow-lg"
              >
                Start 14-Day FREE Trial
              </button>
              <button
                onClick={() => {
                  setShowUpgradeModal(false);
                  setShowUnlockCodeModal(true);
                }}
                className="w-full px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors text-sm"
              >
                I Have an Unlock Code
              </button>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="w-full px-5 py-2 text-sm text-gray-600 hover:text-gray-700"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unlock Code Modal */}
      {showUnlockCodeModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Enter Unlock Code</h3>
            <input
              type="text"
              value={unlockCode}
              onChange={(e) => setUnlockCode(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-4"
              placeholder="EARLYBIRD2025"
            />
            <div className="space-y-3">
              <button
                onClick={() => {
                  if (unlockWithCode(unlockCode)) {
                    setIsPremium(true);
                    setShowUnlockCodeModal(false);
                    setUnlockCode('');
                    alert('Successfully unlocked premium features!');
                  } else {
                    alert('Invalid unlock code. Please try again.');
                  }
                }}
                className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg font-bold hover:bg-primary-700 transition-colors"
              >
                Unlock Premium
              </button>
              <button
                onClick={() => {
                  setShowUnlockCodeModal(false);
                  setUnlockCode('');
                }}
                className="w-full px-6 py-2 text-gray-600 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Import Modal */}
      {showTransactionImport && (
        <TransactionImport
          userId={currentUser?.email}
          currentExpenseCount={expenses.length}
          isPremium={isPremium}
          onUpgradeClick={() => {
            setShowTransactionImport(false);
            setShowUpgradeModal(true);
          }}
          onImport={(transactions) => {
            const newExpenses = transactions.map(t => ({
              id: Date.now().toString() + Math.random(),
              date: t.date,
              amount: t.amount,
              category: t.category,
              description: t.description,
              businessPurpose: `Imported from bank: ${t.merchantName}`,
              receiptUrl: '',
              createdAt: new Date().toISOString()
            }));

            setExpenses(prev => [...newExpenses, ...prev]);
            setShowTransactionImport(false);
            alert(`✅ Successfully imported ${transactions.length} transaction(s)!`);
          }}
          onClose={() => setShowTransactionImport(false)}
        />
      )}

      {/* Profile Settings Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Profile Settings</h2>
                <button
                  onClick={() => setShowProfileModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* User Info */}
              <div className="text-center pb-4 border-b border-gray-200">
                <div className="w-20 h-20 bg-primary-600 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-3">
                  {currentUser?.firstName?.[0]?.toUpperCase() || currentUser?.email[0].toUpperCase()}
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {currentUser?.firstName || 'User'} {currentUser?.lastName || ''}
                </h3>
                <p className="text-gray-500">{currentUser?.email}</p>
              </div>

              {/* Account Status */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Account Status</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Plan:</span>
                    <span className="font-semibold">
                      {isPremium ? (
                        <span className="text-yellow-600">⭐ Premium</span>
                      ) : (
                        <span className="text-gray-600">Free (Limited)</span>
                      )}
                    </span>
                  </div>
                  
                  {/* Subscription Details for Premium Users */}
                  {isPremium && subscriptionStatus?.subscription && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <span className="font-semibold">
                          {getSubscriptionStatusText(subscriptionStatus.subscription.status)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Billing:</span>
                        <span className="font-semibold">
                          {formatAmount(subscriptionStatus.subscription.amount, subscriptionStatus.subscription.currency)}/{subscriptionStatus.subscription.interval}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Next Payment:</span>
                        <span className="font-semibold">
                          {formatSubscriptionDate(subscriptionStatus.subscription.currentPeriodEnd)}
                        </span>
                      </div>
                      {subscriptionStatus.subscription.cancelAtPeriodEnd && (
                        <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-xs">
                          ⚠️ Subscription will cancel on {formatSubscriptionDate(subscriptionStatus.subscription.currentPeriodEnd)}
                        </div>
                      )}
                    </>
                  )}
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600">Bank Connected:</span>
                    <span className="font-semibold">
                      {bankConnected ? (
                        <span className="text-green-600">✓ Yes</span>
                      ) : (
                        <span className="text-gray-400">✗ No</span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Expenses:</span>
                    <span className="font-semibold">{expenses.length} tracked</span>
                  </div>
                  {currentUser?.createdAt && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Member Since:</span>
                      <span className="font-semibold">
                        {new Date(currentUser.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              {!isPremium && (
                <button
                  onClick={() => {
                    setShowProfileModal(false);
                    setShowUpgradeModal(true);
                  }}
                  className="w-full px-6 py-3 bg-yellow-400 text-gray-900 rounded-lg font-bold hover:bg-yellow-300 transition-colors"
                >
                  ⭐ Start FREE Trial (14 Days)
                </button>
              )}

              {bankConnected && (
                <button
                  onClick={() => {
                    if (confirm('Are you sure you want to disconnect your bank?')) {
                      handleDisconnectBank();
                      setShowProfileModal(false);
                    }
                  }}
                  className="w-full px-6 py-3 bg-red-50 text-red-600 rounded-lg font-semibold hover:bg-red-100 transition-colors"
                >
                  Disconnect Bank Account
                </button>
              )}

              <button
                onClick={() => setShowProfileModal(false)}
                className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Assistant - Floating Chat */}
      {currentUser && (
        <AIAssistant
          expenses={expenses}
          income={income}
          auditRiskScore={stats.auditRiskScore}
          riskLevel={riskLevel.level}
          isPremium={isPremium}
          invoices={invoices}
        />
      )}
    </>
  );
}
